import { useState, useEffect } from 'react';
import { usersAPI } from '../../../api/users';

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar usuarios al montar
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Cargando usuarios desde API...');
      
      const response = await usersAPI.getAll();
      const usersData = response.users || response || [];
      
      console.log('✅ Usuarios cargados desde API:', usersData.length);
      setUsers(usersData);
    } catch (err) {
      console.error('❌ Error cargando usuarios:', err);
      setError('Error al cargar usuarios');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Crear usuario
  const createUser = async (userData) => {
    try {
      console.log('➕ Creando usuario:', userData.username);
      
      const response = await usersAPI.create(userData);
      const newUser = response.user || response;
      
      console.log('✅ Usuario creado:', newUser.username);
      
      // Recargar lista
      await loadUsers();
      
      return newUser;
    } catch (err) {
      console.error('❌ Error creando usuario:', err);
      const errorMsg = err.response?.data?.error || 'Error al crear usuario';
      throw new Error(errorMsg);
    }
  };

  // Actualizar usuario
  const updateUser = async (id, userData) => {
    try {
      console.log('✏️ Actualizando usuario:', id);
      
      const response = await usersAPI.update(id, userData);
      const updatedUser = response.user || response;
      
      console.log('✅ Usuario actualizado');
      
      // Recargar lista
      await loadUsers();
      
      return updatedUser;
    } catch (err) {
      console.error('❌ Error actualizando usuario:', err);
      const errorMsg = err.response?.data?.error || 'Error al actualizar usuario';
      throw new Error(errorMsg);
    }
  };

  // Cambiar estado
  const toggleUserStatus = async (id) => {
    try {
      console.log('🔄 Cambiando estado de usuario:', id);
      
      await usersAPI.toggleStatus(id);
      
      console.log('✅ Estado cambiado');
      
      // Recargar lista
      await loadUsers();
    } catch (err) {
      console.error('❌ Error cambiando estado:', err);
      const errorMsg = err.response?.data?.error || 'Error al cambiar estado';
      throw new Error(errorMsg);
    }
  };

  // Resetear contraseña
  const resetPassword = async (id, newPassword) => {
    try {
      console.log('🔑 Reseteando contraseña para usuario:', id);
      
      await usersAPI.resetPassword(id, newPassword);
      
      console.log('✅ Contraseña reseteada');
    } catch (err) {
      console.error('❌ Error reseteando contraseña:', err);
      const errorMsg = err.response?.data?.error || 'Error al resetear contraseña';
      throw new Error(errorMsg);
    }
  };

  // Eliminar usuario
  const deleteUser = async (id) => {
    try {
      console.log('🗑️ Eliminando usuario:', id);
      
      await usersAPI.delete(id);
      
      console.log('✅ Usuario eliminado');
      
      // Recargar lista
      await loadUsers();
    } catch (err) {
      console.error('❌ Error eliminando usuario:', err);
      const errorMsg = err.response?.data?.error || 'Error al eliminar usuario';
      throw new Error(errorMsg);
    }
  };

  // Obtener estadísticas
  const getStats = () => {
    const total = users.length;
    const activos = users.filter(u => u.estado === 'activo').length;
    const inactivos = users.filter(u => u.estado === 'inactivo').length;
    const admins = users.filter(u => u.rol === 'admin').length;
    const rrhh = users.filter(u => u.rol === 'rrhh').length;
    const employees = users.filter(u => u.rol === 'employee').length;

    return {
      total,
      activos,
      inactivos,
      admins,
      supervisores: rrhh, // Mapear 'rrhh' a 'supervisores' para compatibilidad con la UI
      usuarios: employees // Mapear 'employee' a 'usuarios' para compatibilidad con la UI
    };
  };

  // Obtener usuario por ID
  const getUserById = (id) => {
    return users.find(u => u.id === id);
  };

  // Obtener usuario por username
  const getUserByUsername = (username) => {
    return users.find(u => u.username === username);
  };

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    toggleUserStatus,
    resetPassword,
    deleteUser,
    getStats,
    getUserById,
    getUserByUsername,
    refreshUsers: loadUsers
  };
};

export default useUsers;
